import { APIService } from 'src/app/service/api.service';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import bs58 from 'bs58';

import {
  CanReservationDto,
  HostStatistics,
  MyPaymentSearchDto,
  NaverPayCash,
  OrderItemCreateDto,
  PaymentCalendarGroup,
  PaymentCalendarSearchDto,
  PaymentCancel,
  PaymentCancelDto,
  PaymentSearchDto,
  PayMethod,
  ProductRefundPolicyResponse,
} from 'src/app/model/payment';
import { UtilService } from 'src/app/service/util.service';
import { environment } from 'src/environments/environment';
import { Pagination } from '../model/pagination';
import { Payment, PaymentCreateDto, PreOrder, PG } from './../model/payment';
import { Participant, ParticipantBatchUpdateDto } from '../model/participant';

@Injectable({
  providedIn: 'root',
})
export class PaymentService {
  constructor(
    private apiService: APIService,
    private utilService: UtilService,
  ) {}

  create(dto: PaymentCreateDto) {
    return this.apiService.post<Payment>(`/api/payments`, dto);
  }

  getPortOneBankList(): Observable<any[]> {
    return this.apiService.get<any[]>('/api/payments/banks');
  }

  verifyPgPayment(pgName: string, pgOrderId: string): Observable<{ isPaid: boolean; status: string; amount?: number }> {
    return this.apiService.post<{
      isPaid: boolean;
      status: string;
      amount?: number;
    }>('/api/payments/admin/verify-pg-payment', { pgName, pgOrderId });
  }

  // 결제 아이디 생성
  createPGPurchaseId({ useRandomPart = true }: { useRandomPart?: boolean } = {}) {
    // Date.now() → Hex string
    const nowHex = Date.now().toString(16);
    // Convert to Uint8Array
    const timeArr = new Uint8Array(Math.ceil(nowHex.length / 2));
    for (let i = 0; i < timeArr.length; i++) {
      timeArr[i] = parseInt(nowHex.slice(i * 2, i * 2 + 2), 16);
    }
    // Convert base58
    const timePart1 = bs58.encode(timeArr.slice(0, 3));
    const timePart2 = bs58.encode(timeArr.slice(3, timeArr.length));
    // Add random 4 bytes
    const arr = new Uint8Array(4);
    window.crypto.getRandomValues(arr);
    const randomPart = bs58.encode(arr);
    // Add prefix
    const prefix = environment.payment.prefix;
    return `${prefix}${timePart1}-${timePart2}-${useRandomPart ? randomPart : ''}`;
  }

  completePortOne(paymentId: string, deviceId?: string): Observable<Payment> {
    return this.apiService.patch<Payment>(`/api/purchases/portone/complete`, {
      paymentId,
      deviceId,
    });
  }

  getPortOne(paymentId: string): Observable<any> {
    return this.apiService.get<Payment>(`/api/purchases/portone/${paymentId}`);
  }

  getPaymentsCountByProduct(productId: number, excludeCancel?: boolean): Observable<number> {
    const query = this.utilService.setQueryParams({ excludeCancel });
    return this.apiService.get<number>(`/api/payments/by/host/${productId}?${query}`);
  }

  cancelPayment(dto: PaymentCancelDto): Observable<PaymentCancel> {
    return this.apiService.post<PaymentCancel>(`/api/payments/cancel`, dto);
  }

  cancelPortone(dto: PaymentCancelDto): Observable<PaymentCancel> {
    const payload = { ...dto };
    if (dto.bankAccount) {
      payload.bankAccount = {
        bankName: dto.bankAccount.bankName,
        accountNumber: dto.bankAccount.accountNumber,
        accountHolderName: dto.bankAccount.accountHolderName,
      };
    }
    return this.apiService.post<PaymentCancel>(`/api/payments/cancel/portone`, payload);
  }

  cancelNaverPay(dto: PaymentCancelDto): Observable<PaymentCancel> {
    return this.apiService.post<PaymentCancel>(`/api/payments/cancel/naverPay`, dto);
  }

  cancelKakaoPay(dto: PaymentCancelDto): Observable<PaymentCancel> {
    return this.apiService.post<PaymentCancel>(`/api/payments/cancel/kakaoPay`, dto);
  }

  cancelTosspayments(dto: PaymentCancelDto): Observable<PaymentCancel> {
    return this.apiService.post<PaymentCancel>(`/api/payments/cancel/tosspayments`, dto);
  }

  /**
   * PayPal PG 환불 + 취소. 레거시 JWT 로는 backoffice(관리자 세션 전용)를 못 불러, NestJS 어댑터가 같은 이메일의
   * 새 어드민 계정으로 새 어드민 환불 파이프라인을 실행한다. 라우트 부재 404(`Cannot …` 본문) = 미배포(재시도 허용),
   * 422(자동 환불 실행 조건 미충족 — 실행 스위치 꺼짐·PR 프리뷰)·PayPal 표식 400 = 미지원(래치). PG 거절 400·
   * 결과 불명·설정 확인 필요 409 는 서버 문구를 그대로 띄운다.
   */
  cancelPaypal(dto: PaymentCancelDto): Observable<PaymentCancel> {
    return this.apiService.post<PaymentCancel>(`/api/payments/cancel/paypal`, dto);
  }

  cancelFreePayment(paymentId: number, reason: string, reasonCode?: string): Observable<PaymentCancel> {
    return this.apiService.post<PaymentCancel>(`/api/payments/cancel/free`, {
      paymentId,
      reason,
      reasonCode,
    });
  }

  getPurchasedProduct(paymentId: number): Observable<Payment> {
    return this.apiService.get<Payment>(`/api/payments/owner/${paymentId}`);
  }

  getPurchasedProductNonMember(paymentId: number): Observable<Payment> {
    return this.apiService.get<Payment>(`/api/payments/non-member/${paymentId}`);
  }

  getOneByPgOrderId(pgOrderId: string): Observable<Payment> {
    return this.apiService.get<Payment>(`/api/purchases/by-pg-order-id/${pgOrderId}`);
  }

  getNonMemberByPgOrderId(pgOrderId: string): Observable<Payment> {
    return this.apiService.get<Payment>(`/api/purchases/non-member-by-pg-order-id/${pgOrderId}`);
  }

  getPurchasedProducts(search: MyPaymentSearchDto): Observable<Pagination<Payment>> {
    return this.apiService.get<Pagination<Payment>>(`/api/payments/owner/search`, search);
  }

  getPreOrder(preOrderId: number): Observable<PreOrder> {
    return this.apiService.get<PreOrder>(`/api/payments/pre-order/${preOrderId}`);
  }

  getNonMemberPreOrder(name: string, phoneNumber: string, preOrderId: string): Observable<PreOrder> {
    return this.apiService.get<PreOrder>(
      `/api/payments/non-member-pre-order/${preOrderId}?name=${name}&phoneNumber=${phoneNumber}`,
    );
  }

  getPaymentsByHost(search: PaymentSearchDto): Observable<Pagination<Payment>> {
    const query = this.utilService.setQueryParams(search);
    return this.apiService.get<Pagination<Payment>>(`/api/payments/by/host?${query}`);
  }

  getPaymentsAllByHost(search: PaymentSearchDto): Observable<Payment[]> {
    const query = this.utilService.setQueryParams(search);
    return this.apiService.get<Payment[]>(`/api/payments/by/hostAll/?${query}`);
  }

  search(search: PaymentSearchDto): Observable<Pagination<Payment>> {
    const query = this.utilService.setQueryParams(search);
    return this.apiService.get<Pagination<Payment>>(`/api/payments?${query}`);
  }

  getById(paymentId: number): Observable<Payment> {
    return this.apiService.get<Payment>(`/api/payments/${paymentId}`);
  }

  searchAll(search: PaymentSearchDto): Observable<Payment[]> {
    const query = this.utilService.setQueryParams(search);
    return this.apiService.get<Payment[]>(`/api/payments/searchAll?${query}`);
  }

  searchCalendar(search: PaymentCalendarSearchDto): Observable<PaymentCalendarGroup[]> {
    const query = this.utilService.setQueryParams(search);
    return this.apiService.get<PaymentCalendarGroup[]>(`/api/payments/calendar?${query}`);
  }

  getNonMemberPayment(name: string, phoneNumber: string, orderId: string): Observable<Payment> {
    return this.apiService.get<Payment>(
      `/api/payments/non-member?name=${name}&phoneNumber=${phoneNumber}&orderId=${orderId}`,
    );
  }

  isPaidProduct(userId: number, productId: number): Observable<any> {
    return this.apiService.get<any>(`/api/payments/is-paid-product/userId/${userId}/productId/${productId}`);
  }

  editDescription(paymentId: number, description: string) {
    return this.apiService.put(`/api/payments/${paymentId}/description`, {
      description,
    });
  }

  getTotalPriceByMonth(year: number): Observable<{ year: number; month: number; totalPrice: number }[]> {
    return this.apiService.post<{ year: number; month: number; totalPrice: number }[]>(
      `/api/payments/stat/total_price_by_month`,
      { year },
    );
  }

  getTotalPrice() {
    return this.apiService.get<HostStatistics>(`/api/payments/stat/total_price`);
  }

  getGuestCount() {
    return this.apiService.get<HostStatistics>(`/api/payments/stat/guest_count`);
  }

  // TODO: 서버에서 예약 가능한지 체크해야함
  canReservationFromServer(dto: CanReservationDto): Observable<boolean> {
    return this.apiService.post<boolean>('/api/payments/can-reservation/', dto);
  }

  getNaverPayCashReceipt(paymentId: string): Observable<NaverPayCash> {
    return this.apiService.get<NaverPayCash>(`/api/payments/naverPay/cash_receipt?paymentId=${paymentId}`);
  }

  getSaleAgentInfo(saleAgentYear, saleAgentQuarter): Observable<Payment[]> {
    return this.apiService.post<Payment[]>(`/api/payments/sale-agent`, {
      saleAgentYear,
      saleAgentQuarter,
    });
  }

  changeOrderItem(
    paymentId: number,
    orderItems: OrderItemCreateDto[],
    couponId?: number,
    removeCoupon?: boolean,
    point?: number,
    reason?: string,
    refundAccount?: { bank: string; accountNumber: string; accountHolderName: string },
    waiveCancellationFee?: boolean,
    cancellationFeeWaiverReason?: string,
    companyFault?: boolean,
  ) {
    return this.apiService.post<{ payment: Payment; refundAmount: number }>(`/api/payments/change-orderItems`, {
      paymentId,
      orderItems,
      ...(couponId !== undefined && { couponId }),
      ...(removeCoupon !== undefined && { removeCoupon }),
      ...(point !== undefined && { point }),
      ...(reason !== undefined && { reason }),
      ...(refundAccount !== undefined && { refundAccount }),
      ...(waiveCancellationFee && { waiveCancellationFee, cancellationFeeWaiverReason }),
      ...(companyFault && { companyFault }),
    });
  }

  isTicketReservationEnd(id: number): Observable<boolean> {
    return this.apiService.post<boolean>(`/api/payments/tickets/isReservationEnd`, { ticketId: id });
  }

  getRemainReviewFeedCount(): Observable<number> {
    return this.apiService.get<number>('/api/payments/remain-review-feed/count');
  }

  // 결제에 대한 참가자 목록 조회 (관리자용)
  getParticipants(paymentId: number): Observable<Participant[]> {
    return this.apiService.get<Participant[]>(`/api/payments/${paymentId}/participants`);
  }

  // 사용자 본인의 결제에 대한 참가자 목록 조회
  getUserParticipants(paymentId: number): Observable<Participant[]> {
    return this.apiService.get<Participant[]>(`/api/payments/user/${paymentId}/participants`);
  }

  // 비회원 참가자 정보 조회
  getNonMemberParticipants(paymentId: number, name: string, phoneNumber: string): Observable<Participant[]> {
    return this.apiService.get<Participant[]>(
      `/api/payments/non-member/${paymentId}/participants?name=${encodeURIComponent(name)}&phoneNumber=${encodeURIComponent(phoneNumber)}`,
    );
  }

  // 참가자 정보 일괄 업데이트 (관리자용)
  updateParticipantsBatch(paymentId: number, participants: ParticipantBatchUpdateDto[]): Observable<Participant[]> {
    // 백엔드에서 배열이 아닌 객체 { participants: [...] } 형태를 기대함
    return this.apiService.patch<Participant[]>(`/api/payments/${paymentId}/participants`, participants);
  }

  // 사용자 본인의 결제에 대한 참가자 정보 일괄 업데이트
  updateUserParticipantsBatch(paymentId: number, participants: ParticipantBatchUpdateDto[]): Observable<Participant[]> {
    return this.apiService.patch<Participant[]>(`/api/payments/user/${paymentId}/participants`, participants);
  }

  updateNonMemberParticipantsBatch(
    paymentId: number,
    name: string,
    phoneNumber: string,
    participants: ParticipantBatchUpdateDto[],
  ): Observable<Participant[]> {
    // 비회원용 참가자 정보 업데이트 (인증 불필요)
    return this.apiService.patch<Participant[]>(
      `/api/payments/non-member/${paymentId}/participants?name=${encodeURIComponent(name)}&phoneNumber=${encodeURIComponent(phoneNumber)}`,
      participants,
    );
  }

  // 참가자 정보 입력 요청
  requestParticipantInfo(paymentId: number): Observable<{ success: boolean; message: string }> {
    return this.apiService.post<{ success: boolean; message: string }>(
      `/api/payments/${paymentId}/participants/request`,
      {},
    );
  }

  /**
   * Cancellation
   */

  getCancellationReasons(): Observable<string[]> {
    return this.apiService.get<string[]>('/api/purchases/cancellation-reasons');
  }

  getRefundPolicies(productId: number): Observable<ProductRefundPolicyResponse> {
    return this.apiService.get<ProductRefundPolicyResponse>(`/api/purchases/refund-policies?productId=${productId}`);
  }

  getPGName(payMethod: string): PG | null {
    if (payMethod.startsWith('portone_')) return PG.PORTONE;
    if (payMethod === 'kakaoPay') return PG.KAKAO_PAY;
    if (payMethod === 'naverPay') return PG.NAVER_PAY;
    return null;
  }

  getMethodName(payMethod: string): PayMethod | string {
    if (payMethod === 'kakaoPay') return PayMethod.KAKAO_PAY;
    if (payMethod === 'naverPay') return PayMethod.NAVER_PAY;

    // PortOne payment methods
    if (payMethod.startsWith('portone_')) {
      if (payMethod.endsWith('card')) return PayMethod.CARD;
      if (payMethod.endsWith('vtransfer') || payMethod.endsWith('virtual_account')) return PayMethod.VIRTUAL_ACCOUNT;
      if (payMethod.endsWith('transfer')) return PayMethod.TRANSFER;
      if (payMethod === 'portone_kakao_pay') return PayMethod.KAKAO_PAY;
      if (payMethod === 'portone_naver_pay') return PayMethod.NAVER_PAY;
      if (payMethod === 'portone_toss_pay') return PayMethod.TOSS_PAY;
    }

    return payMethod;
  }

  resendNotification(
    paymentId: number,
    target: 'buyer' | 'host' | 'both',
  ): Observable<{
    success: boolean;
    message: string;
    notificationTimes?: {
      buyerNotificationSentAt?: Date;
      hostNotificationSentAt?: Date;
    };
  }> {
    return this.apiService.post(`/api/payments/${paymentId}/resend-notification`, { target });
  }
}
